// L1 — Login con contexto (F0-5).
//
// Layout editorial 2 columnas según mockup docs/design-mockups/screen-l1.jsx:
//   Desktop:
//     - Izquierda 5/12: panel editorial con wordmark serif + tagline + kanji
//       watermark + footer UPG (operado por).
//     - Derecha 7/12: form de login centrado.
//   Mobile:
//     - Stack vertical: editorial arriba (compacto) + form abajo.
//
// Comportamiento (sin cambios respecto a la versión previa):
//   - RHF + Zod
//   - Post-login el redirect lo hace ProtectedRoute / HomeRouter según rol
//   - Errores GoTrue se traducen a mensajes amables en español

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../lib/auth';

// El login acepta CÉDULA o CORREO. Auth (GoTrue) es email-first, así que los
// usuarios sin correo real (líderes/supervisores de planta) se crean con un
// correo sintético `{cedula}@{ID_DOMAIN}`. Si lo que escriben no tiene "@", lo
// tratamos como cédula y le anexamos el dominio; si tiene "@", lo usamos tal cual
// (los que sí tienen correo real). El dominio es configurable por tenant del
// piloto vía env, con default al dominio de Robotpack.
const ID_DOMAIN =
  (import.meta.env.VITE_LOGIN_ID_DOMAIN as string | undefined) ?? 'robotpack.com.co';

function resolverIdentificador(raw: string): string {
  const v = raw.trim();
  return v.includes('@') ? v : `${v}@${ID_DOMAIN}`;
}

const loginSchema = z.object({
  email: z.string().min(1, 'Requerido'),
  password: z.string().min(1, 'Requerido'),
});
type LoginInput = z.infer<typeof loginSchema>;

type LocationState = { from?: { pathname?: string } } | null;

export function LoginPage() {
  const { state, signInWithPassword, signOut } = useAuth();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [recovery, setRecovery] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (state.kind === 'signed_in') {
    const from = (location.state as LocationState)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  // Autenticado en GoTrue pero SIN claims (sin tenant/rol activo): p.ej. su
  // organización fue desactivada, o todavía no le asignaron rol. El login no
  // debe quedar colgado en "Ingresando…" — mostramos un mensaje claro + salir.
  if (state.kind === 'incomplete_claims') {
    return (
      <main className="flex min-h-screen flex-col bg-surface-base md:flex-row">
        <EditorialSide />
        <section className="flex flex-1 items-center justify-center bg-surface-elevated px-6 py-10 md:px-16">
          <div className="flex w-full max-w-sm flex-col gap-4 text-center">
            <h1 className="text-[22px] font-medium tracking-tight text-fg">Sin acceso activo</h1>
            <p className="text-sm leading-relaxed text-fg-secondary">
              Su cuenta no tiene tenants ni roles activos asignados. Puede que su organización esté
              desactivada o que aún no le hayan asignado un rol. Contacte al administrador.
            </p>
            <Button variant="outline" className="mt-2 h-11" onClick={() => void signOut()}>
              Cerrar sesión
            </Button>
          </div>
        </section>
      </main>
    );
  }

  async function onSubmit(values: LoginInput) {
    setServerError(null);
    const { error } = await signInWithPassword(
      resolverIdentificador(values.email),
      values.password,
    );
    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes('invalid login credentials') || msg.includes('invalid grant')) {
        setServerError('Usuario o contraseña incorrectos.');
      } else if (msg.includes('email not confirmed')) {
        setServerError('Su cuenta aún no fue confirmada. Contacte a su administrador.');
      } else {
        setServerError(error.message);
      }
    }
  }

  return (
    <main className="flex min-h-screen flex-col md:flex-row bg-surface-base">
      <EditorialSide />

      <section className="flex flex-1 items-center justify-center bg-surface-elevated px-6 py-10 md:px-16">
        <div className="flex w-full max-w-sm flex-col">
          <h1 className="text-[22px] font-medium leading-tight tracking-tight text-fg">
            Inicie sesión
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
            Ingrese con su cuenta corporativa para acceder a su turno.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 flex flex-col" noValidate>
            <FieldGroup>
              <Label htmlFor="email">Cédula o correo</Label>
              <FieldWithIcon icon="mail">
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  placeholder="Su cédula o su correo"
                  className="h-11 pl-10"
                  aria-invalid={Boolean(errors.email) || undefined}
                  {...register('email')}
                />
              </FieldWithIcon>
              {errors.email ? <FieldError>{errors.email.message}</FieldError> : null}
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="password">Contraseña</Label>
              <FieldWithIcon icon="lock">
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="h-11 pl-10"
                  aria-invalid={Boolean(errors.password) || undefined}
                  {...register('password')}
                />
              </FieldWithIcon>
              {errors.password ? <FieldError>{errors.password.message}</FieldError> : null}
            </FieldGroup>

            {serverError ? (
              <p
                role="alert"
                className="mb-3 rounded border border-crit/30 bg-crit-soft px-3 py-2 text-xs text-crit"
              >
                {serverError}
              </p>
            ) : null}

            <Button type="submit" size="lg" loading={isSubmitting} className="mt-2 h-12 rounded-lg">
              {isSubmitting ? 'Ingresando…' : 'Entrar'}
            </Button>

            <button
              type="button"
              onClick={() => setRecovery(true)}
              className="mt-4 self-center text-[12px] text-fg-secondary underline-offset-2 hover:text-fg hover:underline"
            >
              ¿Olvidó su contraseña?
            </button>
          </form>

          {recovery ? <RecoveryForm onClose={() => setRecovery(false)} /> : null}

          <p className="mt-8 text-center text-[11px] tracking-wide text-fg-tertiary">
            ¿Problemas para entrar? Contacte a su supervisor.
          </p>
        </div>
      </section>
    </main>
  );
}

// =============================================================================
// RecoveryForm — "¿Olvidaste tu contraseña?" → contactar al administrador
// =============================================================================
//
// El reset es MANUAL (lo hace un administrador). No mandamos email de
// recuperación porque la mayoría de los usuarios del piloto entran con cédula y
// un correo sintético sin buzón real: un "te enviamos un enlace" nunca llegaría.

function RecoveryForm({ onClose }: { onClose: () => void }) {
  return (
    <div className="mt-5 rounded-lg border border-border bg-surface-base p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">Restablecer contraseña</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-fg-tertiary hover:text-fg"
        >
          Cerrar
        </button>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-fg-secondary">
        Pídale a su administrador que la restablezca. Por seguridad, el cambio de contraseña se hace
        de forma asistida — no se envía por correo.
      </p>
    </div>
  );
}

// =============================================================================
// EditorialSide — panel izquierdo (desktop) o superior (mobile)
// =============================================================================

function EditorialSide() {
  return (
    <aside className="relative flex flex-shrink-0 flex-col justify-between overflow-hidden border-b border-border bg-surface-base px-7 py-8 md:w-5/12 md:border-r md:border-b-0 md:px-14 md:py-14">
      {/* Kanji watermark 自働 (Jidoka) — sutil, atrás de todo */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 select-none text-fg-disabled opacity-[0.07] md:top-1/2 md:-translate-y-1/2"
        style={{
          fontFamily: "'Hiragino Mincho ProN', 'Yu Mincho', serif",
          fontSize: 'clamp(180px, 30vw, 320px)',
          lineHeight: 0.85,
          letterSpacing: '-10px',
          top: '-40px',
        }}
      >
        自働
      </span>

      <div className="relative z-10 hidden md:block" />

      {/* Brand block */}
      <div className="relative z-10">
        <h2 className="font-serif text-4xl leading-none tracking-tight text-fg md:text-[56px]">
          Jidokap<span className="text-dot">.</span>
        </h2>
        <p className="mt-2 text-xs leading-snug text-fg-secondary md:mt-4 md:max-w-xs md:text-[13px] md:leading-relaxed">
          Operaciones de manufactura con criterio humano.
        </p>
        <p className="mt-5 hidden text-[10px] font-medium uppercase tracking-[0.12em] text-fg-tertiary md:block">
          Plataforma industrial · Colombia
        </p>
      </div>

      {/* Tenant footer (solo desktop) */}
      <div className="relative z-10 hidden border-t border-border pt-5 md:block">
        <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.12em] text-fg-tertiary">
          Operado por
        </p>
        <UPGLogo />
        <p className="mt-2.5 text-[11.5px] tracking-tight text-fg-secondary">
          Universal Pack Group <span className="text-fg-tertiary">·</span> 4 plantas
        </p>
      </div>
    </aside>
  );
}

// =============================================================================
// UPGLogo — placeholder geométrico (no es el branding real de UPG)
// =============================================================================

function UPGLogo() {
  return (
    <svg width={138} height={28} viewBox="0 0 138 30" aria-label="UPG" className="block text-fg">
      <path
        d="M4 4 L4 20 Q4 26 12 26 Q20 26 20 20 L20 4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="square"
      />
      <path
        d="M30 26 L30 4 L40 4 Q46 4 46 10 Q46 16 40 16 L30 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="square"
      />
      <path
        d="M76 6 Q72 4 66 4 Q56 4 56 15 Q56 26 66 26 Q72 26 76 24 L76 16 L68 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="square"
      />
      <rect x={92} y={11} width={8} height={8} fill="currentColor" />
      <rect
        x={104}
        y={11}
        width={8}
        height={8}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      />
      <rect x={116} y={11} width={8} height={8} fill="currentColor" opacity={0.35} />
    </svg>
  );
}

// =============================================================================
// Form helpers
// =============================================================================

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="mb-3.5 flex flex-col gap-1.5">{children}</div>;
}

function FieldError({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-[11px] text-crit">{children}</p>;
}

function FieldWithIcon({ icon, children }: { icon: 'mail' | 'lock'; children: React.ReactNode }) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary"
      >
        <IconMini name={icon} />
      </span>
      {children}
    </div>
  );
}

function IconMini({ name }: { name: 'mail' | 'lock' }) {
  const props = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (name === 'mail') {
    return (
      <svg {...props}>
        <rect x={3} y={5} width={18} height={14} rx={2} />
        <polyline points="3 7 12 13 21 7" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <rect x={5} y={11} width={14} height={9} rx={1.5} />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}
